import * as React from "react";
import {
  CellInfo,
  TableCellRenderer
} from "react-table";
import styled, { css } from "styled-components";
import _ from "lodash";

import { ITerminology } from "src/library/utils/string-manipulations/tokenize-string";
import { CustomTableColumn } from "src/library/components/Tables/Table";
import TableLink from "src/library/components/Tables/TableLink";
import { Text } from "src/library/components/Text";
import { ImageWrapper } from "src/core/shared/components/S3";
import * as globalTableColumns from "src/core/shared/components/TableGlobalColumns";
import {
  appliedDiscountTypePrice,
  formatPrice
} from "src/core/prices/utils/price-helpers";
import BrandedImage from "src/core/shared/components/BrandedImage";
import { IRole } from "src/core/shared/api/roles.api";
import { IProductMaybeWithSubstitution } from "src/core/products/utils";
import LeadTimeGroupFilter from "src/core/products/components/LeadTimeGroupFilter";
import { IDrop } from "src/core/drops/api/drops.api";
import { InstalledModuleType } from "src/utils/constants";

export interface IProductTable {
  GridViewComponent?: (product: IProductMaybeWithSubstitution) => React.ReactNode;
  ActionButton?: TableCellRenderer;
  currentRole: IRole | null;
  pricesVisible: boolean;
  terminology: ITerminology;
  omitColumns?: ProductsTableColumnAcccessors[];
  drop?: IDrop | null;
  installedModules?: InstalledModuleType[];
}

export enum ProductsTableColumnAcccessors {
  Image = "image",
  Description = "description",
  CreatedAt = "createdAt",
  LeadTimeGroup = "leadTimeGroup",
  Prices = "prices"
}
// TODO Select all to be done with the bulk actions task
export const productTableColumns = (params: IProductTable): CustomTableColumn[] => {
  const ProductColumnsAdmin: CustomTableColumn[] = [
    globalTableColumns.id(),
    {
      Header: "Image",
      accessor: "image",
      width: 80,
      filterable: false,
      sortable: false,
      Cell: (cellProps: CellInfo) => {
        return (
          <ImageWrapper className={cellProps.value ? "image-wrapper hasImage" : "placeholder image-wrapper"}>
            <BrandedImage
              s3Key={cellProps.value}
              alt={cellProps.original.description}
              level="public"
            />
          </ImageWrapper>
        );
      }
    },
    globalTableColumns.standardText({
      Header: "Name",
      accessor: "description",
      width: 250,
      Cell: (cellProps: CellInfo) => {
        if (cellProps.value) {
          return (
            <TableLink to={`/products/${cellProps.original.id}`}>
              {cellProps.value}
            </TableLink>
          );
        }

        return null;
      }
    }),
    globalTableColumns.date({
      header: "Created",
      accessor: "createdAt",
      show: false,
      format: "Do MMM YYYY"
    }),
    globalTableColumns.standardText({
      Header: params.terminology?.partNumber.singular.text || "Part number",
      accessor: "partNumber",
      width: 120
    }),
    globalTableColumns.productStatus(),
    globalTableColumns.standardText({
      Header: "Lead time group",
      accessor: "leadTimeGroupId",
      minLength: 1,
      filterable: false,
      sortable: false,
      width: 150,
      Cell: (cellProps: CellInfo) => {
        if (typeof cellProps.value === "object") {
          return (
            <Text>
              {cellProps.value?.customerDescription || cellProps.value?.name}
            </Text>
          );
        } else if (cellProps.value === null) {
          return (
            <Text>
              Made To Order
            </Text>
          );
        }

        return (
          <Text>
            {cellProps.original?.leadTimeGroup?.name || ""}
          </Text>
        );
      },
      Filter: LeadTimeGroupFilter
    }),
    globalTableColumns.actions({ cell: params.ActionButton }),
    {
      Header: () => <></>,
      accessor: "grid",
      resizable: false,
      filterable: false,
      sortable: false,
      Cell: (cellProps: CellInfo) => {
        if (params.GridViewComponent)
          return params.GridViewComponent(cellProps.original);

        return;
      }
    }
  ];

  const ProductColumnsCustomer: CustomTableColumn[] = [
    {
      accessor: "image",
      width: 80,
      filterable: false,
      sortable: false,
      Cell: (cellProps: CellInfo) => {
        return (
          <ImageWrapper className={cellProps.value ? "image-wrapper split-left hasImage" : "placeholder image-wrapper split-left"}>
            <BrandedImage
              s3Key={cellProps.value}
              alt={cellProps.original.description}
              level="public"
            />
          </ImageWrapper>
        );
      }
    },
    globalTableColumns.standardText({
      Header: "Name",
      accessor: "description",
      width: 250,
      Cell: (cellProps: CellInfo) => {
        if (cellProps.value) {
          return (
            <TableLink to={`/products/${cellProps.original.id}`}>
              {cellProps.value}
            </TableLink>
          );
        }

        return null;
      }
    }),
    globalTableColumns.actions({ cell: params.ActionButton }),
    {
      Header: () => <></>,
      accessor: "grid",
      filterable: false,
      sortable: false,
      Cell: (cellProps: CellInfo) => {
        if (params.GridViewComponent)
          return params.GridViewComponent(cellProps.original);

        return;
      }
    }
  ];

  if (params.pricesVisible) {
    ProductColumnsCustomer.splice(2, 0, {
      Header: "Price",
      accessor: "prices",
      Cell: (cellProps: CellInfo) => {
        const originalPrice = cellProps.value[ 0 ].price;

        if (cellProps.value && cellProps.value[ 0 ]) {
          // if discount type exists, apply before formatting otherwise use the original price
          const priceToFormat = appliedDiscountTypePrice(originalPrice, params.drop?.discount);
          const price = formatPrice(priceToFormat);

          return (
            <PriceCell>
              {`£${price}`}

              {" "}

              <sub>
                Each
              </sub>
            </PriceCell>
          );
        }

        return null;
      }
    });

    ProductColumnsAdmin.splice(2, 0, {
      Header: "Price",
      accessor: "prices",
      filterable: false,
      sortable: false,
      width: 60,
      Cell: (cellProps: CellInfo) => {
        const originalPrice = cellProps.value[ 0 ].price;

        if (cellProps.value && cellProps.value[ 0 ]) {
          // if discount type exists, apply before formatting otherwise use the original price
          const priceToFormat = appliedDiscountTypePrice(originalPrice, params.drop?.discount);

          return formatPrice(priceToFormat);
        }

        return null;
      }
    });
  }

  if (params.currentRole) {
    if (params.currentRole.id === 3) {
      if (params.omitColumns) {
        return _.filter(ProductColumnsCustomer, column => !params?.omitColumns?.includes(column.accessor as ProductsTableColumnAcccessors));
      }

      return ProductColumnsCustomer;
    } else {
      if (params.omitColumns) {
        return _.filter(ProductColumnsAdmin, column => !params?.omitColumns?.includes(column.accessor as ProductsTableColumnAcccessors));
      }
    }
  }

  return ProductColumnsAdmin;
};

const PriceCell = styled.p`
  ${({ theme }) => css`
    color: ${theme.colors.primaryDark};
    font-weight: ${theme.fonts.weights.bold};
    font-size: ${theme.fonts.sizes.h2};
  `}
`;
